import React, { useState } from 'react';
import { TbUserCircle, TbStar, TbUserQuestion, TbExternalLink, TbChevronRight } from 'react-icons/tb';
import { useLoginContext } from '../../Contexts/LoginContext';
import MainMenuItem from './MainMenuItem';
import Img from './Img';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useCartHook } from '../../Hooks/CartHook';
import Modal from './Modal';
import Confirm from './Confirm';
import Button from '../Form/Button';
import { HiXMark } from 'react-icons/hi2';
import BoxContentTranslate from './BoxContentTranslate';
import { useTranslation } from 'react-i18next';
import MainMenuItemFidelity from './MainMenuItemFidelity';
import { useLoginHook } from '../../Hooks/LoginHook';
import BoxContentSocial from './BoxContentSocial';
import { handleNavigateToExternalLink, hasLanguages, hasSocialLinks } from '../../Utils/Helpers';

const MainMenu = () => {

    const { t } = useTranslation();
    const { isAuthenticated, user, setShowLoginBasic, isAuthenticatedBySSO, setShowButtonQuickPurchase } = useLoginContext();
    const { setShowMainMenu, company } = useLayoutContext();
    const navigate = useNavigate();
    const { getLinkToHome } = useStoreHook();
    const { removeLogin } = useCartHook();
    const { handleRedirectSSO } = useLoginHook();
    const [ showConfirmLogout, setShowConfirmLogout] = useState<boolean>(false);

    const handleClickProfile = () => {
        navigate('/perfil');
        setShowMainMenu(false);
    }

    const handleLogout = () => {
        setShowConfirmLogout(false);
        removeLogin(false);
        setShowMainMenu(false);
        navigate(getLinkToHome());
    }

    const handleClickLogin = () => {
        if (isAuthenticatedBySSO) {
            handleRedirectSSO();
        } else {
            setShowButtonQuickPurchase(false);
            setShowLoginBasic(true);
        }
    }

    const handleNavigatePage = (page: any) => {
        if (page.external_link) {
            handleNavigateToExternalLink(page.external_link) 
        } else {
            setShowMainMenu(false);
            navigate(`/page/${page.slug}`);
        }
    }

    return (
        <>
            <div className='flex flex-col justify-between h-full gap-2 relative animate-fade-in'>

                <section className='flex flex-col divide-y h-full divide-gray-100 px-4 pt-2'>

                    {isAuthenticated && (
                        <MainMenuItem
                            onClick={handleClickProfile}
                            disabled={!isAuthenticated}
                            icon={<TbUserCircle size={23} className='zoomInIconNoDelay' />}
                            text={user?.first_name || t('menu.main.profile.label')}
                            description={t('menu.main.profile.description')}
                        />
                    )}

                    {/* LOGIN */}
                    <MainMenuItem
                        show={!isAuthenticated}
                        icon={<TbUserQuestion size={25} className='zoomInIconNoDelay' />}
                        text={t('login.label_button_sso')}
                        onClick={handleClickLogin}
                        className='text-custom-primary'
                        withLoading={isAuthenticatedBySSO}
                    />

                    {Boolean(company?.fidelity?.active) && (
                        isAuthenticated && user?.on_fidelity_program
                            ?
                                <MainMenuItemFidelity 
                                    onClick={() => {
                                        navigate('/programa-de-fidelidade');
                                        setShowMainMenu(false);
                                    }}
                                />
                            :   <MainMenuItem
                                    onClick={() => {
                                        navigate('/programa-de-fidelidade');
                                        setShowMainMenu(false);
                                    }}
                                    icon={<TbStar size={23} />}
                                    text={t('menu.main.fidelity.label')}
                                    description={t('menu.main.fidelity.description')}
                                />
                    )}

                    {/* PAGES */}
                    {company?.company_pages?.length > 0 && (
                        <section className='flex flex-col border-none my-4 px-2'>
                            <span className='text-gray-400 text-sm p-2'>{t('menu.main.pages')}</span>
                            <section className='flex flex-col gap-3'>
                                {company.company_pages.map((page: any) => (
                                    <Button
                                        className='rounded-md h-auto py-5'
                                        key={page.id}
                                        onClick={() => handleNavigatePage(page)}
                                    >
                                        <span>{page.title}</span>
                                        <div className='absolute right-10 text-gray-300 text-lg'>
                                            {page.external_link
                                            ? <TbExternalLink className='shrink-0 zoomInIconNoDelay' />
                                                : <TbChevronRight className='shrink-0 zoomInIconNoDelay' />
                                            }
                                        </div>
                                    </Button>
                                ))}
                            </section>
                        </section>
                    )}

                    {(hasLanguages(company) || hasSocialLinks(company)) && (
                        <section className='flex gap-2 items-center border-none my-4'>
                            <BoxContentTranslate onBottom className='border-none py-0' />
                            <BoxContentSocial className='border-none py-0 px-2' />
                        </section>
                    )}

                </section>

                {/* FOOTER */}
                <section className='flex flex-col gap-4 p-4 w-full sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-gray-50'>

                    {/* <BoxContentStoreInfo /> */}

                    <section className='flex items-center justify-between'>
                        <div className='flex items-center gap-1 w-40'>
                            <span className='text-gray-300 text-xs -mb-1.5 text-nowrap'>V {process.env.REACT_APP_VERSION}</span>
                        </div>
                        <div className='flex gap-2 justify-center w-full -mb-1.5 grayscale'>
                            <Img src='/ditti-tagme.png' alt="Ditti by Tagme" className='max-h-12 inline opacity-75' />
                        </div>
                        <div className='flex justify-end  w-40'>
                            <Button rounded onClick={() => setShowMainMenu(false)}>
                                <div><HiXMark size={20} /></div>
                            </Button>
                        </div>
                    </section>

                </section>

            </div>

            <Modal open={showConfirmLogout} className="max-w-sm m-4" onClose={() => setShowConfirmLogout(false)}>
                <Confirm
                    title={t('login.confirm_logout_title')}
                    labelConfirm={t('login.confirm_logout_label_button')}
                    onConfirm={handleLogout}
                    onCancel={() => setShowConfirmLogout(false)}
                />
            </Modal>
        </>
    )
}

export default MainMenu;